import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import moment from 'moment';
import 'moment/locale/th';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { SERVICE_URL } from '../../utils/api';

const STATUS = {
  pending: { label: 'รอเตรียม', color: '#8A938B' },
  acknowledged: { label: 'รับทราบแล้ว', color: '#8A938B' },
  room_ready: { label: 'ห้องพร้อม', color: '#2C6DAF' },
  guest_checkedin: { label: 'รออนุมัติ', color: '#B26B0E' },
  pending_approval: { label: 'รออนุมัติ', color: '#B26B0E' },
  approved: { label: 'อนุมัติแล้ว', color: '#0F8A56' },
  paid: { label: 'จ่ายแล้ว', color: '#0F8A56' },
  declined: { label: 'ไม่สะดวก', color: '#CD3E33' },
};
const statusOf = (s) => STATUS[s] || { label: s, color: '#8A938B' };

// สีสรุปต่อวันในปฏิทิน (เอาสถานะที่ "ก้าวหน้าน้อยสุด" มาโชว์ = งานที่ยังต้องทำ)
const ORDER = ['pending', 'acknowledged', 'room_ready', 'guest_checkedin', 'pending_approval', 'approved', 'paid'];
const daySummaryColor = (statuses = []) => {
  let min = 'paid';
  statuses.forEach((s) => { if (ORDER.indexOf(s) > -1 && ORDER.indexOf(s) < ORDER.indexOf(min)) min = s; });
  return statusOf(min).color;
};

export default function CleanerWork() {
  const search = new URLSearchParams(window.location.search);
  const token = search.get('t');
  const initialDate = search.get('date');

  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const [view, setView] = useState('calendar'); // calendar | day | job
  const [activeMonth, setActiveMonth] = useState(initialDate ? new Date(initialDate) : new Date());
  const [calMap, setCalMap] = useState({});
  const [selectedDate, setSelectedDate] = useState(initialDate || null);
  const [jobs, setJobs] = useState([]);
  const [job, setJob] = useState(null);
  const [phase, setPhase] = useState('prep');
  const [template, setTemplate] = useState([]);
  const [checks, setChecks] = useState({});
  const [busy, setBusy] = useState(false);
  const [uploadingId, setUploadingId] = useState(null);

  const api = axios.create({ baseURL: SERVICE_URL });
  const cw = useCallback((path, opts = {}) => {
    const sep = path.includes('?') ? '&' : '?';
    return api({ url: `cleaner-web/${path}${sep}t=${encodeURIComponent(token || '')}`, ...opts });
    // eslint-disable-next-line
  }, [token]);

  const loadCalendar = useCallback((monthDate) => {
    const month = moment(monthDate).format('YYYY-MM');
    cw(`my-calendar?month=${month}`).then((r) => {
      const map = {};
      (r.data || []).forEach((d) => { map[d.date] = d; });
      setCalMap(map);
    }).catch(() => {});
  }, [cw]);

  const openDay = useCallback((date) => {
    setSelectedDate(date);
    cw(`my-jobs?date=${date}`).then((r) => { setJobs(r.data || []); setView('day'); }).catch(() => {});
  }, [cw]);

  useEffect(() => {
    if (!token) { setError('ลิงก์ไม่ถูกต้อง (ไม่มีรหัส)'); return; }
    cw('me').then((r) => {
      setMe(r.data);
      loadCalendar(activeMonth);
      if (initialDate) openDay(initialDate);
    }).catch(() => setError('ลิงก์ไม่ถูกต้องหรือหมดอายุ กรุณาเปิดจากลิงก์ในไลน์อีกครั้ง'));
    // eslint-disable-next-line
  }, []);

  useEffect(() => { if (token && me) loadCalendar(activeMonth); }, [activeMonth]); // eslint-disable-line

  const openJob = (id) => {
    cw(`job/${id}`).then((r) => {
      setJob(r.data.job); setPhase(r.data.phase); setTemplate(r.data.checklist_template || []);
      const init = {};
      (r.data.checklist_template || []).forEach((it) => { init[it.id] = { result: 'pass', note: '', photo_url: '' }; });
      setChecks(init);
      setView('job');
    }).catch(() => {});
  };

  const acknowledge = (id) => cw(`job/${id}/acknowledge`, { method: 'post' }).then(() => openDay(selectedDate)).catch(() => {});

  const setResult = (id, result) => setChecks((c) => ({ ...c, [id]: { ...c[id], result } }));
  const setNote = (id, note) => setChecks((c) => ({ ...c, [id]: { ...c[id], note } }));
  const onPickPhoto = (id, file) => {
    if (!file) return;
    setUploadingId(id);
    const fd = new FormData(); fd.append('file', file);
    cw('upload-photo', { method: 'post', data: fd, headers: { 'Content-Type': 'multipart/form-data' } })
      .then((r) => setChecks((c) => ({ ...c, [id]: { ...c[id], photo_url: r.data.url } })))
      .catch(() => {}).finally(() => setUploadingId(null));
  };

  const submit = () => {
    if (!job || busy) return;
    setBusy(true);
    const checklist = template.map((it) => ({
      item_id: it.id, label: it.label,
      result: checks[it.id]?.result || 'pass',
      note: checks[it.id]?.note || '',
      photo_url: checks[it.id]?.photo_url || '',
    }));
    const ep = phase === 'checkin' ? 'checkin' : 'prep';
    cw(`job/${job.id}/${ep}`, { method: 'post', data: { checklist } })
      .then(() => { setBusy(false); loadCalendar(activeMonth); openDay(selectedDate); })
      .catch(() => setBusy(false));
  };

  const anyFail = template.some((it) => checks[it.id]?.result === 'fail');

  // ---------- styles ----------
  const S = {
    // paddingTop เผื่อ header ของ LINE in-app browser ที่อยู่ชิดด้านบน (แยก property กัน shorthand+comma พัง)
    page: { boxSizing: 'border-box', width: '100%', maxWidth: 480, margin: '0 auto', paddingTop: 76, paddingRight: 16, paddingBottom: 48, paddingLeft: 16, fontFamily: 'inherit', color: '#1A211D', minHeight: '100vh', overflowX: 'hidden' },
    bar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 },
    back: { flexShrink: 0, border: '1px solid #DBE2DC', background: '#fff', borderRadius: 10, padding: '8px 12px', fontWeight: 700, color: '#0F8A56', whiteSpace: 'nowrap' },
    h: { fontSize: 20, fontWeight: 800, margin: 0 },
    sub: { fontSize: 13, color: '#586159' },
    card: { boxSizing: 'border-box', border: '1.5px solid #DBE2DC', borderRadius: 14, padding: 16, marginBottom: 12, background: '#fff' },
    bigBtn: (bg) => ({ boxSizing: 'border-box', width: '100%', border: 'none', borderRadius: 16, padding: 20, fontSize: 20, fontWeight: 800, color: '#fff', background: bg, marginTop: 12 }),
    toggleWrap: { display: 'flex', border: '1.5px solid #CBD4CC', borderRadius: 999, overflow: 'hidden' },
    tg: (on, kind) => ({ border: 'none', padding: '10px 16px', fontSize: 16, fontWeight: 800, minWidth: 56, background: on ? (kind === 'pass' ? '#0F8A56' : '#B26B0E') : 'transparent', color: on ? '#fff' : '#8A938B' }),
    pill: (c) => ({ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 999, fontSize: 13, fontWeight: 700, color: c, background: c + '22' }),
  };

  if (error) {
    return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80 }}>
      <div style={{ fontSize: 40 }}>🔒</div>
      <p style={{ marginTop: 12, color: '#586159' }}>{error}</p>
    </div>;
  }
  if (!me) return <div style={{ ...S.page, textAlign: 'center', paddingTop: 80, color: '#586159' }}>กำลังโหลด…</div>;

  // ---------- CALENDAR ----------
  if (view === 'calendar') {
    return (
      <div style={S.page}>
        <style>{`.react-calendar{width:100%!important;max-width:100%;border:none;font-family:inherit}`}</style>
        <div style={{ marginBottom: 10 }}>
          <h1 style={S.h}>สวัสดีค่ะ พี่{me.firstname} 🧹</h1>
          <div style={S.sub}>ปฏิทินงานของฉัน — แตะวันที่มีจุดสีเพื่อดูงาน</div>
        </div>
        <Calendar
          locale="th-TH"
          activeStartDate={activeMonth}
          onActiveStartDateChange={({ activeStartDate }) => activeStartDate && setActiveMonth(activeStartDate)}
          onClickDay={(d) => { const ds = moment(d).format('YYYY-MM-DD'); if (calMap[ds]) openDay(ds); }}
          tileContent={({ date, view: v }) => {
            if (v !== 'month') return null;
            const ds = moment(date).format('YYYY-MM-DD');
            const info = calMap[ds];
            if (!info) return null;
            return <div style={{ marginTop: 2, display: 'flex', justifyContent: 'center' }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', background: daySummaryColor(info.statuses), borderRadius: 999, padding: '1px 6px' }}>{info.count}</span>
            </div>;
          }}
          value={null}
        />
        <div style={{ marginTop: 16, display: 'flex', flexWrap: 'wrap', gap: '6px 14px', fontSize: 12, color: '#586159' }}>
          {[['รอเตรียม', '#8A938B'], ['ห้องพร้อม', '#2C6DAF'], ['รออนุมัติ', '#B26B0E'], ['จ่ายแล้ว', '#0F8A56']].map(([l, c]) => (
            <span key={l}><span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: 9, background: c, marginRight: 5 }} />{l}</span>
          ))}
        </div>
      </div>
    );
  }

  // ---------- DAY ----------
  if (view === 'day') {
    return (
      <div style={S.page}>
        <div style={S.bar}>
          <button style={S.back} onClick={() => setView('calendar')}>‹ ปฏิทิน</button>
          <div><h1 style={{ ...S.h, fontSize: 17 }}>งานวันที่ {moment(selectedDate).format('D MMM')}</h1><div style={S.sub}>เฉพาะงานของฉัน</div></div>
        </div>
        {jobs.length === 0 && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>ไม่มีงานวันนี้</div>}
        {jobs.map((j) => {
          const st = statusOf(j.status);
          const done = ['guest_checkedin', 'pending_approval', 'approved', 'paid'].includes(j.status);
          return (
            <div key={j.id} style={S.card} onClick={() => !done && openJob(j.id)}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 18, fontWeight: 800 }}>{j.room?.name_th}</div>
                  <div style={{ fontSize: 12, color: '#8A938B' }}>{j.booking?.contact_name}</div>
                </div>
                <span style={S.pill(st.color)}>{st.label}</span>
                {!done && <span style={{ color: '#0F8A56', fontSize: 22, fontWeight: 800 }}>›</span>}
              </div>
              {j.status === 'pending' && (
                <button style={{ ...S.bigBtn('#0F8A56'), padding: 12, fontSize: 15, marginTop: 10 }}
                  onClick={(e) => { e.stopPropagation(); acknowledge(j.id); }}>รับทราบ</button>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ---------- JOB (checklist) ----------
  const isPrep = phase !== 'checkin';
  return (
    <div style={S.page}>
      <div style={S.bar}>
        <button style={S.back} onClick={() => openDay(selectedDate)}>‹ งานวันนั้น</button>
        <div>
          <h1 style={{ ...S.h, fontSize: 17 }}>{job?.room?.name_th} · {isPrep ? 'เตรียมห้อง' : 'ต้อนรับลูกค้า'}</h1>
          <div style={S.sub}>{isPrep ? 'แตะ "ไม่" เฉพาะข้อที่มีปัญหา' : 'ทำครบแล้วกดยืนยัน'}</div>
        </div>
      </div>

      {template.length === 0 && <div style={{ ...S.card, textAlign: 'center', color: '#8A938B' }}>ยังไม่มีรายการเช็ค (ตั้งค่าใน CMS)</div>}

      {template.map((it) => {
        const c = checks[it.id] || { result: 'pass' };
        const fail = c.result === 'fail';
        return (
          <div key={it.id} style={{ ...S.card, marginBottom: 10, borderColor: fail ? '#B26B0E' : '#DBE2DC', background: fail ? '#FBEED4' : '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, fontSize: 17, fontWeight: 700 }}>{it.label}</div>
              <div style={S.toggleWrap}>
                <button style={S.tg(!fail, 'pass')} onClick={() => setResult(it.id, 'pass')}>✓</button>
                <button style={S.tg(fail, 'fail')} onClick={() => setResult(it.id, 'fail')}>ไม่</button>
              </div>
            </div>
            {fail && (
              <div style={{ marginTop: 10 }}>
                <input value={c.note || ''} onChange={(e) => setNote(it.id, e.target.value)} placeholder="พิมพ์สั้นๆ เช่น มดขึ้นที่นอน"
                  style={{ boxSizing: 'border-box', width: '100%', border: '1.5px solid #CBD4CC', borderRadius: 10, padding: 11, fontSize: 15 }} />
                <label style={{ display: 'block', marginTop: 8, border: '1.5px dashed #CBD4CC', borderRadius: 10, padding: 11, fontSize: 14, fontWeight: 700, color: c.photo_url ? '#0F8A56' : '#586159', textAlign: 'center' }}>
                  {uploadingId === it.id ? 'กำลังอัปโหลด…' : c.photo_url ? '📷 แนบรูปแล้ว ✓ (แตะเปลี่ยน)' : '📷 ถ่าย/แนบรูป'}
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => onPickPhoto(it.id, e.target.files[0])} />
                </label>
              </div>
            )}
          </div>
        );
      })}

      <button disabled={busy} style={S.bigBtn(isPrep && anyFail ? '#B26B0E' : '#0F8A56')} onClick={submit}>
        {busy ? 'กำลังส่ง…' : isPrep ? (anyFail ? '⚠️ แจ้งปัญหา + ส่งงานห้องพร้อม' : '✅ ห้องพร้อม (ส่งงาน)') : '✅ คอนเฟิร์มส่งงาน'}
      </button>
      <div style={{ textAlign: 'center', fontSize: 12.5, color: '#8A938B', marginTop: 8 }}>
        {isPrep && anyFail ? 'ปัญหาจะถูกส่งให้แอดมิน — ค่าจ้างไม่ถูกตัด' : 'กดปุ่มเดียวจบ'}
      </div>
    </div>
  );
}
